import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CreditCard, CheckCircle2, Sparkles, ShieldCheck, Lock } from "lucide-react";

/**
 * Loads the Razorpay Checkout SDK script (once) and resolves when window.Razorpay
 * is available.
 */
function loadRazorpay() {
    return new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(window.Razorpay);
        const existing = document.getElementById("razorpay-checkout-js");
        if (existing) {
            existing.addEventListener("load", () => resolve(window.Razorpay));
            existing.addEventListener("error", reject);
            return;
        }
        const s = document.createElement("script");
        s.id = "razorpay-checkout-js";
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.async = true;
        s.onload = () => resolve(window.Razorpay);
        s.onerror = reject;
        document.body.appendChild(s);
    });
}

export default function Subscription() {
    const { user, refreshUser } = useAuth();
    const [status, setStatus] = useState(null);
    const [processing, setProcessing] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        const { data } = await api.get("/subscription/status");
        setStatus(data);
    };
    useEffect(() => { load(); }, []);

    const startCheckout = async () => {
        setProcessing(true);
        try {
            const Razorpay = await loadRazorpay();
            const { data: session } = await api.post("/subscription/checkout");

            const options = {
                key: session.razorpay_key_id,
                amount: session.amount_paise,
                currency: session.currency,
                order_id: session.razorpay_order_id,
                name: "Zapperus",
                description: "Annual borrowing membership",
                prefill: {
                    name: session.prefill?.name || "",
                    email: session.prefill?.email || "",
                },
                notes: {
                    product: "zapperus_annual",
                },
                theme: { color: "#F04E23" },
                modal: {
                    ondismiss: () => setProcessing(false),
                },
                handler: async (response) => {
                    try {
                        const fd = new FormData();
                        fd.append("razorpay_order_id", response.razorpay_order_id);
                        fd.append("razorpay_payment_id", response.razorpay_payment_id);
                        fd.append("razorpay_signature", response.razorpay_signature);
                        await api.post("/subscription/verify", fd);
                        toast.success("Membership activated!", {
                            description: "You can now borrow books for a full year.",
                            action: { label: "Browse", onClick: () => navigate("/app/storefront") },
                        });
                        await Promise.all([load(), refreshUser()]);
                    } catch (e) {
                        toast.error(formatError(e), { description: "If money was debited, it will be refunded automatically within 5-7 days." });
                    } finally {
                        setProcessing(false);
                    }
                },
            };

            const rzp = new Razorpay(options);
            rzp.on("payment.failed", (resp) => {
                toast.error("Payment failed", { description: resp?.error?.description || "Please try again." });
                setProcessing(false);
            });
            rzp.open();
        } catch (e) {
            toast.error(formatError(e));
            setProcessing(false);
        }
    };

    if (!status) return <div className="max-w-3xl mx-auto px-6 py-10 text-ink/50">Loading…</div>;

    const priceTotal = status.price?.total ?? 352.82;
    const priceBase = status.price?.base ?? 299;
    const priceGst = status.price?.gst ?? 53.82;

    return (
        <div className="max-w-3xl mx-auto px-6 py-10">
            <div className="mb-8">
                <div className="label-mini">Membership</div>
                <h1 className="font-serif text-4xl font-semibold">Your Zapperus plan</h1>
                <p className="text-ink/60 mt-2">
                    First month is free. After that, ₹{priceBase} + GST per year lets you borrow books.
                    Owners can always post books and earn coins — no membership needed.
                </p>
            </div>

            {/* Status card */}
            <StatusCard status={status} />

            {/* CTA row */}
            {status.state !== "subscribed" && (
                <div className="mt-6 bg-white border border-parchment rounded-2xl p-6 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="font-serif text-2xl font-semibold">Annual borrowing plan</div>
                            <p className="text-sm text-ink/60 mt-1">Unlimited reservations for one year.</p>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-3xl font-bold text-ink">₹{priceTotal}</div>
                            <div className="text-xs text-ink/50 mt-1">₹{priceBase} + ₹{priceGst} GST</div>
                        </div>
                    </div>
                    <button
                        data-testid="sub-checkout-btn"
                        onClick={startCheckout}
                        disabled={processing}
                        className="btn-primary mt-6 w-full sm:w-auto"
                    >
                        <CreditCard className="h-4 w-4" /> {processing ? "Opening checkout…" : `Pay ₹${priceTotal}`}
                    </button>
                    <p className="text-xs text-ink/50 mt-3 flex items-center gap-1.5">
                        <Lock className="h-3 w-3" /> Secure payments powered by Razorpay. Cards, UPI, netbanking &amp; wallets accepted.
                    </p>
                </div>
            )}
        </div>
    );
}

function StatusCard({ status }) {
    const map = {
        trial: { title: "Free trial active", tone: "library", icon: <Sparkles className="h-5 w-5" /> },
        subscribed: { title: "Membership active", tone: "library", icon: <CheckCircle2 className="h-5 w-5" /> },
        expired: { title: "Trial has ended", tone: "brick", icon: <ShieldCheck className="h-5 w-5" /> },
    };
    const meta = map[status.state] || map.expired;
    const border = meta.tone === "library" ? "border-library/30 bg-library/5" : "border-brick/30 bg-brick/5";
    const iconWrap = meta.tone === "library" ? "bg-library/10 text-library" : "bg-brick/10 text-brick";
    const expires = status.subscription_expires_at || status.trial_ends_at;
    return (
        <div data-testid="sub-status-card" className={`border rounded-2xl p-6 ${border}`}>
            <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${iconWrap}`}>{meta.icon}</div>
                <div>
                    <div className="font-serif text-2xl font-semibold">{meta.title}</div>
                    <div className="text-sm text-ink/60 mt-1">
                        {status.can_borrow ? (
                            <>You can borrow books. {status.days_remaining > 0 && `${status.days_remaining} day${status.days_remaining !== 1 ? "s" : ""} remaining.`}</>
                        ) : (
                            <>Borrowing is disabled. Posting and earning coins is still active.</>
                        )}
                    </div>
                    {expires && (
                        <div className="text-xs text-ink/50 mt-2">
                            {status.state === "subscribed" ? "Renews on" : "Ends on"} {new Date(expires).toLocaleDateString()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
